import { colors } from '@aloft/toolkit-sui';

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  DefaultLegendContent,
  DefaultLegendContentProps,
  Legend,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';

import { GraphTitle } from '../../../components/graphs/components/GraphTitle.tsx';
import { GraphXAxis } from '../../../components/graphs/components/GraphXAxis.tsx';
import { GraphYAxis } from '../../../components/graphs/components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../../../components/graphs/components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../../../components/graphs/components/SimpleGraphTooltip.tsx';
import {
  GroupedVacantLandSalesChartData,
  VacantLandSalesGraphProps,
} from '../../../components/graphs/costApproach/types.ts';
import { groupVacantLandSalesData } from '../../../components/graphs/costApproach/utils.ts';
import { selectSiteCostPerSf } from '../../../redux/features/selectors/selectSiteCostPerSf.ts';
import { useAppSelector } from '../../../redux/hooks.ts';

const LegendContent = ({ payload, ...props }: DefaultLegendContentProps) => (
  <DefaultLegendContent
    {...props}
    payload={[
      ...payload!,
      {
        value: 'Subject Property',
        type: 'line',
        color: colors.graphChart4,
      },
      {
        value: '25th-75th Percentile',
        type: 'rect',
        color: colors.graphAreaBackground,
      },
    ]}
  />
);

export const DistributionOfVacantLandGraph = ({
  disableAnimations = false,
  width,
  height,
  vacantLandSalesChartData,
}: VacantLandSalesGraphProps) => {
  const subjectCostPerSF = useAppSelector((state) =>
    selectSiteCostPerSf(state, 'subject'),
  )?.toDollarLike();

  if (!subjectCostPerSF) return null;

  const data = groupVacantLandSalesData(vacantLandSalesChartData);

  const minCostPerSF = Math.min(...data.map((d) => d.costPerSF));
  const maxCostPerSF = Math.max(...data.map((d) => d.costPerSF));

  const p25CostPerSF = minCostPerSF + (maxCostPerSF - minCostPerSF) * 0.25;
  const p75CostPerSF = minCostPerSF + (maxCostPerSF - minCostPerSF) * 0.75;

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <ComposedChart
        data={data}
        width={width}
        height={height}
        margin={{ top: 32, left: 8, bottom: 8, right: 8 }}
        style={{ background: colors.graphBackground, borderRadius: 8 }}
      >
        <GraphTitle>Distribution of Vacant Land $ / SF</GraphTitle>
        <CartesianGrid strokeDasharray="3 3" />
        <Legend verticalAlign="top" align="right" content={LegendContent} />

        <SimpleGraphTooltip<GroupedVacantLandSalesChartData>
          disableAnimations={disableAnimations}
          titleKey="costPerSF"
          rows={[
            {
              label: 'Frequency',
              key: 'percentage',
              color: 'graphChart',
              valueFormatter: (v) => `${v?.toLocaleString() ?? '-'}%`,
            },
          ]}
        />

        <GraphXAxis name="Cost Per SF" dataKey="costPerSF" scale="linear" label="$ / SF" />
        <GraphYAxis label="Frequency" tickFormatter={(value) => `${value}%`} />

        <Bar
          name="Vacant Land Sale"
          dataKey="percentage"
          fill={colors.graphChart}
          isAnimationActive={!disableAnimations}
        />

        <ReferenceLine x={subjectCostPerSF} stroke={colors.graphChart4} />
        <ReferenceArea x1={p25CostPerSF} x2={p75CostPerSF} fill={colors.graphAreaBackground} />
      </ComposedChart>
    </ResponsiveGraphWrapper>
  );
};
